import React, { useState } from "react";
import { GraduationCap, FileUp, ChevronRight } from "lucide-react";
import coursesIntroVideo from "@/assets/shattaraai_courses_intro.mp4";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { CreateCourseWizard } from "@/components/courses/CreateCourseWizard";
import { cn } from "@/lib/utils";

export const DashboardHeroMastery: React.FC = () => {
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitialStep, setWizardInitialStep] = useState(0);

  const openWizard = (step: number) => {
    setWizardInitialStep(step);
    setWizardOpen(true);
  };

  return (
    <div className="flex w-full flex-col items-center">
      <div className="mb-5 flex flex-col items-center gap-2">
        <h2 className="text-xl text-center sm:text-3xl font-medium xl:text-4xl">
          What do you want to master?
        </h2>
        <p className="text-base text-muted-foreground text-center">
          Create a personalized course by chatting with AI.{" "}
          <Popover open={learnMoreOpen} onOpenChange={setLearnMoreOpen}>
            <PopoverTrigger asChild>
              <button className="inline-flex cursor-pointer items-center gap-1 underline underline-offset-4 text-muted-foreground transition-colors hover:text-foreground">
                Learn more
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-arrow-up-right h-3.5 w-3.5"
                  aria-hidden="true"
                >
                  <path d="M7 7h10v10" />
                  <path d="M7 17 17 7" />
                </svg>
              </button>
            </PopoverTrigger>
            <PopoverContent align="center" sideOffset={8} className="w-[calc(100vw-2rem)] sm:w-[440px] rounded-2xl p-5 leading-relaxed">
              <div className="mb-1 flex items-start justify-between">
                <h4 className="flex items-center gap-2 font-medium whitespace-nowrap">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-correct" />
                  Test your knowledge on everything
                </h4>
              </div>
              <p className="mb-4 text-sm text-muted-foreground">
                Build custom courses, get lesson, concept, and question breakdowns, track your progress, and review weak spots.
              </p>
              <div className="mb-3">
                <video
                  src={coursesIntroVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="h-auto w-full rounded-xl border border-border object-cover"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setLearnMoreOpen(false)}
                  className="inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 rounded-lg px-3 text-sm"
                >
                  Got it!
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </p>
      </div>

      {/* Two CTA Cards */}
      <div className="z-10 w-full text-center md:max-w-lg xl:max-w-2xl flex flex-col">
        <div className="grid w-full grid-cols-1 gap-2.5 sm:flex sm:items-center sm:justify-center sm:gap-3">
          <MasteryCard
            onClick={() => openWizard(0)}
            title="Create Course"
            description="Describe what you want to master"
            accent="violet"
            Icon={GraduationCap}
          />
          <MasteryCard
            onClick={() => openWizard(1)}
            title="Import Course"
            description="Upload PDFs, audio, or Youtube URLs"
            accent="amber"
            Icon={FileUp}
          />
        </div>
      </div>

      <CreateCourseWizard
        isOpen={wizardOpen}
        onOpenChange={setWizardOpen}
        initialStep={wizardInitialStep}
      />
    </div>
  );
};

type MasteryAccent = "violet" | "amber";

const MASTERY_ACCENT_STYLES: Record<MasteryAccent, { bg: string; fg: string }> = {
  violet: {
    bg: "bg-violet-500/10 dark:bg-violet-400/10",
    fg: "text-violet-600 dark:text-violet-400",
  },
  amber: {
    bg: "bg-amber-500/10 dark:bg-amber-400/10",
    fg: "text-amber-600 dark:text-amber-400",
  },
};

interface MasteryCardProps {
  onClick: () => void;
  title: string;
  description: string;
  accent: MasteryAccent;
  Icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>;
}

const MasteryCard: React.FC<MasteryCardProps> = ({
  onClick,
  title,
  description,
  accent,
  Icon,
}) => {
  const styles = MASTERY_ACCENT_STYLES[accent];
  return (
    <div className="w-full flex-1 sm:w-1/2">
      <div
        onClick={onClick}
        className={cn(
          "group relative cursor-pointer rounded-2xl border border-border bg-card text-card-foreground shadow-sm transition-all duration-200",
          "active:scale-[0.98] active:bg-muted/40",
          "sm:rounded-3xl sm:active:scale-100 sm:active:bg-card sm:hover:shadow-md"
        )}
      >
        {/* Mobile layout: horizontal tile with icon chip + chevron */}
        <div className="flex items-center gap-4 p-4 sm:hidden">
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
              styles.bg
            )}
          >
            <Icon className={cn("h-5 w-5", styles.fg)} aria-hidden />
          </div>
          <div className="flex min-w-0 flex-1 flex-col text-left">
            <h3 className="text-base font-semibold leading-tight text-foreground">
              {title}
            </h3>
            <p className="truncate text-sm text-muted-foreground">
              {description}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" aria-hidden />
        </div>

        {/* Desktop layout: vertical card (preserves original look) */}
        <div className="hidden h-[112px] flex-col items-start justify-center gap-y-1 px-5 py-4 sm:flex">
          <Icon
            className="mb-2 h-6 w-6 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
            aria-hidden
          />
          <div className="flex flex-col justify-center text-left">
            <h3 className="text-base font-medium text-muted-foreground transition-colors group-hover:text-foreground">
              {title}
            </h3>
            <p className="text-sm text-left text-muted-foreground/70 transition-colors group-hover:text-muted-foreground">
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
